/* Dafodila - Product Description (tabs on desktop, accordion on mobile, Read More on both) */
(() => {
  if (customElements.get('dafodila-product-info')) return;

  const DESKTOP = '(min-width: 750px)';
  const INNER_SCROLL = '(min-width: 990px)'; // the theme scrolls inside .page-wrapper from here up
  const DURATION = 320; // Read More opening animation
  const MAX_ANIMATED = 1.5; // open instantly when the extra content is taller than 1.5 screens
  const SLACK = 40; // content only this much taller than the limit is shown in full
  const PRELOAD_DISTANCE = '1400px 0px'; // start loading images this far before the section is on screen

  class DafodilaProductInfo extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.tabs = Array.from(this.querySelectorAll('.dpi__tab'));
      this.items = Array.from(this.querySelectorAll('.dpi__item'));
      this.limitDesktop = parseInt(this.dataset.collapsed, 10) || 220;
      this.limitMobile = parseInt(this.dataset.collapsedMobile, 10) || 260;
      this.textMore = this.dataset.readMore || 'Read More';
      this.textLess = this.dataset.readLess || 'Read Less';
      this.mq = window.matchMedia(DESKTOP);
      this.mqScroll = window.matchMedia(INNER_SCROLL);
      this.motion = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.near = false;

      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.activate(i));
        tab.addEventListener('keydown', (event) => this.onTabKey(event, i));
        // Start loading a tab's images as soon as the shopper shows intent
        const preload = () => this.loadImages(this.items[i]);
        tab.addEventListener('pointerenter', preload, { passive: true });
        tab.addEventListener('focus', preload);
      });

      this.items.forEach((item) => {
        const acc = item.querySelector('.dpi__acc-btn');
        const more = item.querySelector('.dpi__more');
        if (acc) acc.addEventListener('click', () => this.toggle(item));
        if (more) {
          more.addEventListener('click', () =>
            item.classList.contains('is-expanded') ? this.collapse(item) : this.expand(item)
          );
        }
      });

      this.onMode = () => this.applyMode();
      this.onScrollMode = () => this.watchViewport();
      this.listen(this.mq, this.onMode);
      this.listen(this.mqScroll, this.onScrollMode);

      // Re-check heights when images load or the text reflows
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => this.scheduleMeasure());
        this.items.forEach((item) => {
          const body = item.querySelector('.dpi__body');
          if (body) this.ro.observe(body);
        });
      }

      this.applyMode();
      this.watchViewport();
      this.classList.add('is-ready');
    }

    disconnectedCallback() {
      if (this.ro) this.ro.disconnect();
      if (this.io) this.io.disconnect();
      this.unlisten(this.mq, this.onMode);
      this.unlisten(this.mqScroll, this.onScrollMode);
      if (this._raf) cancelAnimationFrame(this._raf);
    }

    listen(mq, fn) {
      if (!mq) return;
      if (mq.addEventListener) mq.addEventListener('change', fn);
      else mq.addListener(fn);
    }

    unlisten(mq, fn) {
      if (!mq) return;
      if (mq.removeEventListener) mq.removeEventListener('change', fn);
      else mq.removeListener(fn);
    }

    get desktop() {
      return this.mq.matches;
    }

    get limit() {
      return this.desktop ? this.limitDesktop : this.limitMobile;
    }

    part(item, name) {
      return item.querySelector(name);
    }

    // Desktop shows the active tab; mobile shows every open accordion row
    isVisible(item) {
      return this.desktop ? item.classList.contains('is-active') : item.classList.contains('is-open');
    }

    /* ---------- Scrolling ---------- */

    // The element that actually scrolls the page (the theme uses .page-wrapper on large screens)
    scrollRoot() {
      let el = this.parentElement;
      while (el && el !== document.body && el !== document.documentElement) {
        const overflowY = getComputedStyle(el).overflowY;
        if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) return el;
        el = el.parentElement;
      }
      return null;
    }

    // Move the page by `delta` px in one step, even if the theme turned on smooth scrolling
    scrollByInstant(delta) {
      if (!delta || Math.abs(delta) < 1) return;
      const root = this.scrollRoot();
      const target = root || document.scrollingElement || document.documentElement;
      const previous = target.style.scrollBehavior;
      target.style.scrollBehavior = 'auto';
      if (root) root.scrollTop += delta;
      else window.scrollBy(0, delta);
      target.style.scrollBehavior = previous;
    }

    /* ---------- Image loading ---------- */

    watchViewport() {
      if (this.near) return;
      if (this.io) this.io.disconnect();
      if (!('IntersectionObserver' in window)) {
        this.near = true;
        this.loadVisible();
        return;
      }
      this.io = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.near = true;
          this.io.disconnect();
          this.loadVisible();
        },
        { root: this.scrollRoot(), rootMargin: PRELOAD_DISTANCE }
      );
      this.io.observe(this);
    }

    loadVisible() {
      this.items.forEach((item) => {
        if (this.isVisible(item)) this.loadImages(item);
      });
    }

    loadImages(item) {
      if (!item || item._loaded) return;
      item._loaded = true;
      item.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
      });
    }

    /* ---------- Tabs / accordion ---------- */

    applyMode() {
      const desktop = this.desktop;
      this.items.forEach((item, i) => {
        const panel = this.part(item, '.dpi__panel');
        const acc = this.part(item, '.dpi__acc-btn');
        const tab = this.tabs[i];
        if (!panel) return;
        if (desktop && tab) {
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('aria-labelledby', tab.id);
        } else if (acc) {
          panel.setAttribute('role', 'region');
          panel.setAttribute('aria-labelledby', acc.id);
        }
        this.reset(item);
      });
      if (this.near) this.loadVisible();
      this.scheduleMeasure();
    }

    activate(index, focus = false) {
      this.tabs.forEach((tab, i) => {
        const on = i === index;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
      });
      this.items.forEach((item, i) => {
        const on = i === index;
        if (!on) this.reset(item);
        item.classList.toggle('is-active', on);
      });
      this.loadImages(this.items[index]);
      if (focus && this.tabs[index]) this.tabs[index].focus();
      this.scheduleMeasure();
    }

    onTabKey(event, i) {
      const last = this.tabs.length - 1;
      let next = null;
      switch (event.key) {
        case 'ArrowRight':
          next = i === last ? 0 : i + 1;
          break;
        case 'ArrowLeft':
          next = i === 0 ? last : i - 1;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = last;
          break;
        default:
          return;
      }
      event.preventDefault();
      this.activate(next, true);
    }

    // Mobile accordion row. A row always reopens in its folded (Read More) state.
    toggle(item) {
      const open = !item.classList.contains('is-open');
      if (!open) this.reset(item);
      item.classList.toggle('is-open', open);
      const acc = this.part(item, '.dpi__acc-btn');
      if (acc) acc.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) {
        this.loadImages(item);
        this.scheduleMeasure();
      }
    }

    reset(item) {
      const clip = this.part(item, '.dpi__clip');
      this.stopAnimation(item);
      item.classList.remove('is-expanded');
      if (clip) {
        clip.style.maxHeight = '';
        clip.style.overflow = '';
        clip.style.transition = '';
      }
      this.setLabel(item, false);
    }

    setLabel(item, expanded) {
      const more = this.part(item, '.dpi__more');
      if (!more) return;
      more.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      const text = more.querySelector('.dpi__more-text');
      if (text) text.textContent = expanded ? this.textLess : this.textMore;
    }

    /* ---------- Read More ---------- */

    scheduleMeasure() {
      if (this._raf) return;
      this._raf = requestAnimationFrame(() => {
        this._raf = null;
        this.measure();
      });
    }

    // Show Read More only when the content is really taller than the limit
    measure() {
      const limit = this.limit;
      this.items.forEach((item) => {
        const clip = this.part(item, '.dpi__clip');
        const body = this.part(item, '.dpi__body');
        const more = this.part(item, '.dpi__more');
        if (!clip || !body || !more) return;
        if (!this.isVisible(item) || item._busy) return;
        if (item.classList.contains('is-expanded')) {
          more.hidden = false;
          return;
        }

        const tall = body.scrollHeight > limit + SLACK;
        clip.classList.toggle('is-clamped', tall);
        more.hidden = !tall;
      });
    }

    stopAnimation(item) {
      if (item && item._finish) item._finish();
    }

    // Smooth opening for normal-length content
    animateOpen(item, clip, from, to) {
      item._busy = true;
      clip.style.overflow = 'hidden';
      clip.style.maxHeight = `${from}px`;
      void clip.offsetHeight; // apply the start height before animating
      clip.style.transition = `max-height ${DURATION}ms cubic-bezier(0.25, 0.8, 0.25, 1)`;
      clip.style.maxHeight = `${to}px`;

      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        clip.removeEventListener('transitionend', onEnd);
        clip.style.transition = '';
        clip.style.maxHeight = '';
        clip.style.overflow = '';
        item._busy = false;
        item._finish = null;
      };
      const onEnd = (event) => {
        if (event.target === clip && event.propertyName === 'max-height') finish();
      };
      clip.addEventListener('transitionend', onEnd);
      const timer = setTimeout(finish, DURATION + 100);
      item._finish = finish;
    }

    expand(item) {
      const clip = this.part(item, '.dpi__clip');
      if (!clip) return;
      this.stopAnimation(item);
      this.loadImages(item);

      const from = clip.getBoundingClientRect().height;
      clip.classList.remove('is-clamped'); // fade overlay eases out via CSS
      const to = clip.scrollHeight;
      item.classList.add('is-expanded');
      this.setLabel(item, true);

      // Very long content opens in one step: animating thousands of pixels is what causes jank on phones
      const tooLong = to - from > window.innerHeight * MAX_ANIMATED;
      if (this.motion && !tooLong) this.animateOpen(item, clip, from, to);
    }

    // Read Less: fold in one step and keep the button exactly where the shopper's finger is,
    // so the page never jumps and the phone only has to lay the page out once.
    collapse(item) {
      const clip = this.part(item, '.dpi__clip');
      const more = this.part(item, '.dpi__more');
      if (!clip || !more) return;
      this.stopAnimation(item);

      const before = more.getBoundingClientRect().top;

      item.classList.remove('is-expanded');
      this.setLabel(item, false);
      clip.style.transition = '';
      clip.style.maxHeight = '';
      clip.style.overflow = '';
      clip.classList.add('is-clamped'); // fade overlay eases in via CSS

      const after = more.getBoundingClientRect().top;
      this.scrollByInstant(after - before);
    }
  }

  customElements.define('dafodila-product-info', DafodilaProductInfo);
})();
